import React, { useCallback, useEffect, useState } from "react";

import Link from "next/link";
import { useForm, SubmitHandler, Controller } from "react-hook-form";
import Linkify from "react-linkify";

//import { CreatorData } from "data/storefront";
import { StorefrontData } from "data/storefront";
import { Button, LargeInput } from "studio-design-system";
import { isMemberName } from "typescript";

import { collectEmail } from "~/api/userApi";
import { ImageWithBlur } from "~/components/common/ImageWithBlur/ImageWithBlur";
import { useAuth } from "~/hooks/auth/useAuth";
import { GENERIC_ERROR_MESSAGE } from "~/util/commonErrors";

import { emailValidRegex } from "../../../../util/validateEmail";
import * as baseStyles from "../FormLayout.css";

interface IFormInput {
  email: string;
}

interface Props {
  storefrontData: StorefrontData;
  setIsUserFollowing: (isFollowing: boolean) => void;
}
export const FollowForm = ({ storefrontData, setIsUserFollowing }: Props): React.ReactElement => {
  const { user } = useAuth();
  const [submitErrorMessage, setSubmitErrorMessage] = useState<string | undefined>();
  const [isEmailAdded, setIsEmailAdded] = useState(false);
  const [isWaiting, setIsWaiting] = useState(false);

  const {
    register,
    control,
    formState: { errors, isSubmitting },
    handleSubmit,
  } = useForm<IFormInput>({
    defaultValues: {
      email: "",
    },
  });

  const onSubmit: SubmitHandler<IFormInput> = useCallback(
    async (data) => {
      if (isEmailAdded) {
        console.log("email added and submitting, so should dismiss modal");
        return;
      }
      const { email } = data;

      console.log("on submit email! ", email);

      const storefrontSlug = storefrontData.slug;
      const extraTags = ["follow-form", storefrontData.id];

      const response = await collectEmail({ email, classInterestedIn: storefrontSlug, extraTags });
      if (response.error) {
        setSubmitErrorMessage(GENERIC_ERROR_MESSAGE);
        return;
      }
      setIsEmailAdded(true);
      setIsUserFollowing(true);
      setIsWaiting(false);
    },
    [isEmailAdded, setIsUserFollowing, storefrontData.id, storefrontData.slug],
  );

  useEffect(() => {
    if (user) {
      setIsEmailAdded(true);
      onSubmit({ email: user.email });
    }
  }, [onSubmit, user]);

  return (
    <div className={baseStyles.container}>
      <div className={baseStyles.topSection}>
        <div className={isEmailAdded ? baseStyles.titleRowCenter : baseStyles.titleRow}>
          <div className={baseStyles.titleImg}>
            <ImageWithBlur
              src={storefrontData.welcomePosterImageURL}
              width={32}
              height={32}
              layout={"fixed"}
              objectFit={"cover"}
              alt={storefrontData.title}
            />
          </div>
          {isEmailAdded ? (
            <h3 className={baseStyles.title}>
              {"You've followed "} {storefrontData.creatorName} 🎉
            </h3>
          ) : (
            <h3 className={baseStyles.title}>Follow {storefrontData.creatorName}</h3>
          )}
        </div>

        {!isEmailAdded && (
          <div className={baseStyles.subTitle}>
            Get notified when {storefrontData.creatorName} releases new behind the scenes videos or
            when new promotional offers are available.
          </div>
        )}
      </div>

      {!isEmailAdded && (
        <>
          <div className={baseStyles.formControls}>
            <Controller
              control={control}
              rules={{
                pattern: {
                  value: emailValidRegex,
                  message:
                    "This email address looks invalid. If we’re wrong, please contact support@studio.com.",
                },
                required: { value: true, message: "You must provide an email." },
              }}
              render={({ field: { onChange, value } }) => (
                <LargeInput
                  type="email"
                  placeholder="Email"
                  errorMessage={errors.email?.message}
                  shouldAutoFocus
                  autoComplete="email"
                  onChange={onChange}
                  value={value}
                />
              )}
              name="email"
            />
          </div>

          {submitErrorMessage && (
            <div className={baseStyles.generalFormError}>
              <Linkify>{submitErrorMessage}</Linkify>
            </div>
          )}

          <div className={baseStyles.bottomAltActionContainer}>
            <span>
              By continuing, you agree to Studio&apos;s{" "}
              <Link href="/terms">
                <span className={baseStyles.link}>Terms</span>
              </Link>{" "}
              and{" "}
              <Link href="/privacy">
                <span className={baseStyles.link}>Privacy Policy</span>
              </Link>
            </span>
          </div>
        </>
      )}

      {isEmailAdded && (
        <div className={baseStyles.followedDescription}>
          Stay tuned for their new behind the scenes videos
        </div>
      )}

      {!isEmailAdded && (
        <div>
          <Button
            isLoading={isSubmitting}
            onClick={handleSubmit(onSubmit)}
            className={baseStyles.primaryButton}
          >
            {isEmailAdded ? "Explore their Studio" : "Follow"}
          </Button>
        </div>
      )}
    </div>
  );
};
